/**
 * CardMesh3D.js - High-Fidelity 3D Playing Cards Engine for Crazy Cribbage
 * Generates procedural thin-bevel cardstock meshes, high-DPI DynamicTexture faces and backs,
 * radial ergonomic hand fanning, starter decks, crib stacks, play piles, and pointer physics.
 */

export class CardMesh3D {
  /**
   * Static texture cache for shared card faces and backs across identical cards.
   * @type {Map<string, BABYLON.DynamicTexture>}
   */
  static textureCache = new Map();

  /**
   * Standard poker playing card dimensions in Babylon world units.
   */
  static CARD_WIDTH = 11.0;
  static CARD_HEIGHT = 15.4;
  static CARD_THICKNESS = 0.12;

  /**
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {Object} cardData - { r: '5', s: 'H', id: '5H_0', ... }
   * @param {Object} [options={}]
   */
  constructor(scene, BABYLON, cardData, options = {}) {
    this.scene = scene;
    this.BABYLON = BABYLON;
    this.card = cardData || { r: 'A', s: 'S', id: 'AS_0' };
    this.options = options;

    this.width = options.width || CardMesh3D.CARD_WIDTH;
    this.height = options.height || CardMesh3D.CARD_HEIGHT;
    this.thickness = options.thickness || CardMesh3D.CARD_THICKNESS;
    this.backStyle = options.backStyle || 'classic-blue';

    this.owner = options.owner || 'p1';
    this.isLocal = (options.isLocal != null) ? Boolean(options.isLocal) : (this.owner === 'p1');
    this.faceUp = (options.faceUp != null) ? Boolean(options.faceUp) : this.isLocal;
    this.isSelected = false;
    this.isHovered = false;
    this.isInteractive = options.interactive !== false;
    this.isDeck = Boolean(options.isDeck);
    this.isCutFan = Boolean(options.isCutFan);
    this.cutIndex = options.cutIndex != null ? options.cutIndex : null;

    this._baseY = 0;
    this._hoverElev = 2.4;
    this._selectElev = 4.8;
    this._animating = false;
    this._animObserver = null;

    // Callbacks
    this.onClick = options.onClick || null;
    this.onHover = options.onHover || null;

    this.buildMesh();
  }

  /**
   * Returns underlying card data object.
   * @type {Object}
   */
  get cardData() {
    return this.card;
  }

  /**
   * Unique identifier for the card used in reconciliation and object reuse.
   * @type {string}
   */
  get cardId() {
    return this.card?.id || `${this.card?.rankName || this.card?.r || this.card?.rank || 'A'}${this.card?.suit || this.card?.s || 'S'}`;
  }

  /**
   * Whether the card face is legitimately visible/revealed to the player.
   * If false, front face texture is replaced with card back to prevent peeking when the 3D room is rotated.
   * @type {boolean}
   */
  get isFaceRevealed() {
    return Boolean(this.faceUp && (this.isLocal || this.options.faceUp === true || this.options.revealed === true));
  }

  /**
   * Normalizes rank and suit strings from any engine format.
   * @param {Object} c
   * @returns {{ rank: string, suit: string, isRed: boolean, cid: string }}
   */
  static normalizeCard(c) {
    if (!c) return { rank: 'A', suit: 'S', isRed: false, cid: 'AS' };
    const isWild = Boolean(c.wild || c.isWild || c.r === 0 || c.rank === 0);
    if (isWild && !c.playAs) {
      return { rank: '★', suit: 'J', isRed: false, cid: c.id || 'JOKER' };
    }
    let rank = String(c.rankName || (c.playAs ? c.playAs.r : c.r) || c.rank || 'A').toUpperCase();
    if (rank === '1') rank = 'A';
    if (rank === '11') rank = 'J';
    if (rank === '12') rank = 'Q';
    if (rank === '13') rank = 'K';

    let suit = String((c.playAs ? c.playAs.s : c.suit) || c.s || 'S').toUpperCase().charAt(0);
    if (!['S', 'H', 'D', 'C'].includes(suit)) {
      if (suit === '♠') suit = 'S';
      else if (suit === '♥') suit = 'H';
      else if (suit === '♦') suit = 'D';
      else if (suit === '♣') suit = 'C';
      else suit = 'S';
    }

    const isRed = (suit === 'H' || suit === 'D');
    const cid = c.id || `${rank}${suit}`;
    return { rank, suit, isRed, cid };
  }

  /**
   * Builds the composite 3D playing card mesh:
   * Front face plane, Back face plane, and white cardstock core with edge thickness.
   */
  buildMesh() {
    const { scene, BABYLON } = this;
    const cid = this.card.id || `${this.card.r || 'A'}${this.card.s || 'S'}`;

    // Root Transform Node for positioning, fanning, and animation
    this.rootNode = new BABYLON.TransformNode(`card3D_${cid}`, scene);

    const doubleSide = (BABYLON.Mesh && BABYLON.Mesh.DOUBLESIDE != null) ? BABYLON.Mesh.DOUBLESIDE : 2;

    // 1. Front Face Plane (facing -Z towards camera at local z = -thickness/2)
    this.frontMesh = BABYLON.MeshBuilder.CreatePlane(`cardFront_${cid}`, {
      width: this.width,
      height: this.height,
      sideOrientation: doubleSide
    }, scene);
    this.frontMesh.position.set(0, 0, -this.thickness / 2 - 0.005);
    this.frontMesh.rotation.set(0, 0, 0);
    this.frontMesh.parent = this.rootNode;
    this.frontMesh.isPickable = this.isInteractive;
    this.frontMesh.receiveShadows = true;

    this.frontMat = new BABYLON.PBRMaterial(`matCardFront_${cid}`, scene);
    this.frontMat.roughness = 0.38;
    this.frontMat.metallic = 0.02;
    this.frontMat.backFaceCulling = false;
    this.frontMat.transparencyMode = 0; // BABYLON.PBRMaterial.PBRMATERIAL_OPAQUE
    this.frontMat.alpha = 1.0;

    this.updateCardTextures();
    this.frontMesh.material = this.frontMat;

    // 2. Back Face Plane (facing +Z away from camera at local z = +thickness/2)
    this.backMesh = BABYLON.MeshBuilder.CreatePlane(`cardBack_${cid}`, {
      width: this.width,
      height: this.height,
      sideOrientation: doubleSide
    }, scene);
    this.backMesh.position.set(0, 0, this.thickness / 2 + 0.005);
    this.backMesh.rotation.set(0, Math.PI, 0); // Inverted to face backwards (+Z)
    this.backMesh.parent = this.rootNode;
    this.backMesh.isPickable = this.isInteractive;
    this.backMesh.receiveShadows = true;

    this.backMat = new BABYLON.PBRMaterial(`matCardBack_${cid}_${this.backStyle}`, scene);
    this.backMat.roughness = 0.32;
    this.backMat.metallic = 0.05;
    this.backMat.backFaceCulling = false;
    this.backMat.transparencyMode = 0; // BABYLON.PBRMaterial.PBRMATERIAL_OPAQUE
    this.backMat.alpha = 1.0;

    const backTex = CardMesh3D.getCardBackTexture(scene, BABYLON, this.backStyle);
    if (backTex) {
      backTex.hasAlpha = false;
      this.backMat.albedoTexture = backTex;
    } else {
      this.backMat.albedoColor = new BABYLON.Color3(0.12, 0.22, 0.55);
    }
    this.backMesh.material = this.backMat;

    // 3. Cardstock Core Box (edges and thickness)
    this.coreMesh = BABYLON.MeshBuilder.CreateBox(`cardCore_${cid}`, {
      width: this.width - 0.05,
      height: this.height - 0.05,
      depth: this.thickness
    }, scene);
    this.coreMesh.position.set(0, 0, 0);
    this.coreMesh.parent = this.rootNode;
    this.coreMesh.isPickable = false;

    this.coreMat = new BABYLON.PBRMaterial(`matCardCore_${cid}`, scene);
    this.coreMat.albedoColor = new BABYLON.Color3(0.96, 0.96, 0.94); // Ivory cardstock
    this.coreMat.roughness = 0.6;
    this.coreMesh.material = this.coreMat;

    // 4. Subtle Selection Highlight Rim
    this.highlightMesh = BABYLON.MeshBuilder.CreatePlane(`cardHighlight_${cid}`, {
      width: this.width + 0.8,
      height: this.height + 0.8,
      sideOrientation: doubleSide
    }, scene);
    this.highlightMesh.position.set(0, 0, -this.thickness / 2 - 0.012);
    this.highlightMesh.parent = this.rootNode;
    this.highlightMesh.isPickable = false;
    this.highlightMesh.isVisible = false;

    this.highlightMat = new BABYLON.PBRMaterial(`matCardHighlight_${cid}`, scene);
    this.highlightMat.emissiveColor = new BABYLON.Color3(1.0, 0.85, 0.2); // Warm gold outline
    this.highlightMat.alpha = 0.65;
    this.highlightMesh.material = this.highlightMat;

    // Link mesh userData back to this CardMesh3D instance for raycasting/picking
    this.frontMesh.metadata = { cardMesh: this, card: this.card, isDeck: this.isDeck, isCutFan: this.isCutFan, cutIndex: this.cutIndex };
    this.backMesh.metadata = { cardMesh: this, card: this.card, isDeck: this.isDeck, isCutFan: this.isCutFan, cutIndex: this.cutIndex };

    // Set initial orientation (faceUp or faceDown)
    if (!this.faceUp) {
      this.rootNode.rotation.y = Math.PI;
    }
  }

  /**
   * Clears the static texture cache.
   */
  static clearTextureCache() {
    CardMesh3D.textureCache.forEach(tex => {
      try {
        if (tex && !tex.isDisposed && typeof tex.dispose === 'function') tex.dispose();
      } catch (_) {}
    });
    CardMesh3D.textureCache.clear();
  }

  /**
   * Retrieves or generates a high-DPI procedural DynamicTexture for a card face.
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {Object} card
   * @returns {BABYLON.DynamicTexture|null}
   */
  static getCardFaceTexture(scene, BABYLON, card) {
    if (!BABYLON || !BABYLON.DynamicTexture) return null;
    const { rank, suit, cid } = CardMesh3D.normalizeCard(card);
    const isCut = Boolean(card && card.isCut);
    const cacheKey = `face_${rank}_${suit}_${isCut ? 'cut' : 'std'}`;

    if (CardMesh3D.textureCache.has(cacheKey)) {
      const cached = CardMesh3D.textureCache.get(cacheKey);
      if (cached && (typeof cached.getScene !== 'function' || cached.getScene() === scene) && !cached.isDisposed) {
        return cached;
      }
      CardMesh3D.textureCache.delete(cacheKey);
    }

    const tex = new BABYLON.DynamicTexture(`texFace_${cacheKey}`, { width: 512, height: 716 }, scene, false);
    tex.hasAlpha = false;
    const ctx = tex.getContext ? tex.getContext() : null;
    if (ctx) {
      CardMesh3D.drawCardFace(ctx, rank, suit, card.isCut);
      if (typeof tex.update === 'function') tex.update();
    }

    CardMesh3D.textureCache.set(cacheKey, tex);
    return tex;
  }

  /**
   * Retrieves or generates a high-DPI procedural DynamicTexture for a card back.
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {string} [backStyle='classic-blue']
   * @returns {BABYLON.DynamicTexture|null}
   */
  static getCardBackTexture(scene, BABYLON, backStyle = 'classic-blue') {
    if (!BABYLON || !BABYLON.DynamicTexture) return null;
    const cacheKey = `back_${backStyle}`;

    if (CardMesh3D.textureCache.has(cacheKey)) {
      const cached = CardMesh3D.textureCache.get(cacheKey);
      if (cached && (typeof cached.getScene !== 'function' || cached.getScene() === scene) && !cached.isDisposed) {
        return cached;
      }
      CardMesh3D.textureCache.delete(cacheKey);
    }

    const tex = new BABYLON.DynamicTexture(`texBack_${cacheKey}`, { width: 512, height: 716 }, scene, false);
    tex.hasAlpha = false;
    const ctx = tex.getContext ? tex.getContext() : null;
    if (ctx) {
      CardMesh3D.drawCardBack(ctx, backStyle);
      if (typeof tex.update === 'function') tex.update();
    }

    CardMesh3D.textureCache.set(cacheKey, tex);
    return tex;
  }

  /**
   * Draws a crisp, high-DPI authentic playing card face onto an HTML Canvas 2D context.
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} rank - 'A', '2'..'10', 'J', 'Q', 'K', '★'
   * @param {string} suit - 'S', 'H', 'D', 'C', 'J'
   * @param {boolean} [isCut=false]
   */
  static drawCardFace(ctx, rank, suit, isCut = false) {
    if (!ctx) return;
    const w = 512;
    const h = 716;

    // 1. Crisp Solid White Cardstock Background (100% opaque)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    // Subtle Card Perimeter Border
    ctx.strokeStyle = isCut ? '#eab308' : '#cbd5e1';
    ctx.lineWidth = isCut ? 8 : 4;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(8, 8, w - 16, h - 16, 20);
    } else {
      ctx.rect(8, 8, w - 16, h - 16);
    }
    ctx.stroke();

    const suitSymbolMap = { S: '♠', H: '♥', D: '♦', C: '♣', J: '★' };
    const suitSymbol = suitSymbolMap[suit] || '♠';
    const isRed = (suit === 'H' || suit === 'D');
    const primaryColor = isRed ? '#dc2626' : '#0f172a';

    // 2. Corner Indices (Top-Left and Bottom-Right rotated)
    const drawCornerIndex = (x, y, angle = 0) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);

      ctx.fillStyle = primaryColor;
      ctx.textAlign = 'center';

      // Rank Text
      ctx.font = 'bold 54px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(rank, 0, 0);

      // Suit Symbol under Rank
      ctx.font = '42px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(suitSymbol, 0, 42);

      ctx.restore();
    };

    drawCornerIndex(52, 68, 0);                  // Top-Left
    drawCornerIndex(w - 52, h - 68, Math.PI);    // Bottom-Right (180° inverted)

    // 3. Center Artwork (Spot Cards vs Court Cards vs Joker)
    if (rank === '★') {
      ctx.save();
      ctx.fillStyle = '#ca8a04';
      ctx.font = 'bold 120px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('★', w / 2, h / 2 - 20);
      ctx.font = 'bold 44px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#9333ea';
      ctx.fillText('WILD JOKER', w / 2, h / 2 + 80);
      ctx.restore();
    } else if (['J', 'Q', 'K'].includes(rank)) {
      // Court Card Portrait Artwork Frame
      CardMesh3D.drawCourtCardCenter(ctx, rank, suit, suitSymbol, primaryColor, w, h);
    } else {
      // Spot / Pip Card (A through 10)
      CardMesh3D.drawPipCardCenter(ctx, rank, suitSymbol, primaryColor, w, h);
    }

    // 4. Cut Card Badge Indicator
    if (isCut) {
      ctx.save();
      ctx.fillStyle = '#ca8a04';
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('STARTER CUT', w / 2, h - 28);
      ctx.restore();
    }
  }

  /**
   * Draws traditional pip configurations (Ace through 10).
   */
  static drawPipCardCenter(ctx, rank, suitSymbol, color, w, h) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const cx = w / 2;
    const cy = h / 2;

    if (rank === 'A') {
      // Grand Centerpiece Ace
      ctx.font = '160px "Segoe UI", Roboto, sans-serif';
      ctx.fillText(suitSymbol, cx, cy);

      // Decorative Filigree Luster for Ace of Spades
      if (suitSymbol === '♠') {
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(cx, cy, 100, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else {
      // Multi-Pip Standard Layouts
      ctx.font = '72px "Segoe UI", Roboto, sans-serif';
      const pipCoords = CardMesh3D.getPipCoordinates(rank, w, h);
      pipCoords.forEach(([px, py, flip]) => {
        ctx.save();
        ctx.translate(px, py);
        if (flip) ctx.rotate(Math.PI);
        ctx.fillText(suitSymbol, 0, 0);
        ctx.restore();
      });
    }
    ctx.restore();
  }

  /**
   * Calculates normalized coordinate positions for 2 through 10 card pips.
   */
  static getPipCoordinates(rank, w, h) {
    const lx = w * 0.33;
    const rx = w * 0.67;
    const cx = w * 0.50;

    const ty = h * 0.22;
    const tmy = h * 0.36;
    const my = h * 0.50;
    const bmy = h * 0.64;
    const by = h * 0.78;

    switch (rank) {
      case '2':
        return [[cx, ty, false], [cx, by, true]];
      case '3':
        return [[cx, ty, false], [cx, my, false], [cx, by, true]];
      case '4':
        return [[lx, ty, false], [rx, ty, false], [lx, by, true], [rx, by, true]];
      case '5':
        return [[lx, ty, false], [rx, ty, false], [cx, my, false], [lx, by, true], [rx, by, true]];
      case '6':
        return [[lx, ty, false], [rx, ty, false], [lx, my, false], [rx, my, false], [lx, by, true], [rx, by, true]];
      case '7':
        return [[lx, ty, false], [rx, ty, false], [cx, tmy, false], [lx, my, false], [rx, my, false], [lx, by, true], [rx, by, true]];
      case '8':
        return [[lx, ty, false], [rx, ty, false], [cx, tmy, false], [lx, my, false], [rx, my, false], [cx, bmy, true], [lx, by, true], [rx, by, true]];
      case '9':
        return [[lx, ty, false], [rx, ty, false], [lx, tmy, false], [rx, tmy, false], [cx, my, false], [lx, bmy, true], [rx, bmy, true], [lx, by, true], [rx, by, true]];
      case '10':
        return [[lx, ty, false], [rx, ty, false], [cx, tmy - 15, false], [lx, tmy + 15, false], [rx, tmy + 15, false], [lx, bmy - 15, true], [rx, bmy - 15, true], [cx, bmy + 15, true], [lx, by, true], [rx, by, true]];
      default:
        return [[cx, my, false]];
    }
  }

  /**
   * Draws authentic two-way royal portrait emblem for Jack, Queen, and King.
   */
  static drawCourtCardCenter(ctx, rank, suit, suitSymbol, color, w, h) {
    ctx.save();
    const frameW = 280;
    const frameH = 440;
    const fx = (w - frameW) / 2;
    const fy = (h - frameH) / 2;

    // Double Gold Filigree Border
    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 4;
    ctx.strokeRect(fx, fy, frameW, frameH);
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(fx + 6, fy + 6, frameW - 12, frameH - 12);

    // Inner Parchment Royal Backing
    ctx.fillStyle = '#fafaf9';
    ctx.fillRect(fx + 8, fy + 8, frameW - 16, frameH - 16);

    // Royal Sash & Insignia
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, 70, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = '76px "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(suitSymbol, w / 2, h / 2);

    // Crown / Royal Crest
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 36px "Segoe UI", Roboto, sans-serif';
    ctx.fillText(rank === 'K' ? '♔ KING' : (rank === 'Q' ? '♕ QUEEN' : '♗ JACK'), w / 2, fy + 42);
    ctx.fillText(rank === 'K' ? '♔ KING' : (rank === 'Q' ? '♕ QUEEN' : '♗ JACK'), w / 2, fy + frameH - 36);

    ctx.restore();
  }

  /**
   * Generates procedural pattern artwork for the back of the card.
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} backStyle
   */
  static drawCardBack(ctx, backStyle = 'classic-blue') {
    if (!ctx) return;
    const w = 512;
    const h = 716;

    // 1. Crisp White Cardstock Margin (100% solid opaque)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    // White Border Inset
    const margin = 26;
    const innerW = w - margin * 2;
    const innerH = h - margin * 2;

    ctx.save();
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(margin, margin, innerW, innerH, 16) : ctx.rect(margin, margin, innerW, innerH);
    ctx.clip();

    // 2. Thematic Pattern Fill
    if (backStyle === 'classic-red' || backStyle === 'vegas-crimson') {
      // Crimson Bicycle Rosette / Diamond Weave
      ctx.fillStyle = '#991b1b';
      ctx.fillRect(margin, margin, innerW, innerH);

      ctx.strokeStyle = '#b91c1c';
      ctx.lineWidth = 2;
      for (let x = -w; x < w * 2; x += 16) {
        ctx.beginPath();
        ctx.moveTo(x, 0); ctx.lineTo(x + h, h); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, h); ctx.lineTo(x + h, 0); ctx.stroke();
      }

      // Center Rosette
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, 54, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#991b1b';
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, 44, 0, Math.PI * 2);
      ctx.fill();
    } else if (backStyle === 'royal-gold' || backStyle === 'obsidian-carbon') {
      // Onyx Carbon & Gold Filigree
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(margin, margin, innerW, innerH);

      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 1.5;
      for (let x = margin; x < w - margin; x += 24) {
        ctx.beginPath();
        ctx.arc(x, h / 2, 80, 0, Math.PI);
        ctx.stroke();
      }

      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 3;
      ctx.strokeRect(margin + 12, margin + 12, innerW - 24, innerH - 24);
    } else if (backStyle === 'emerald-diamond') {
      // Emerald Green Argyle with Gold Flecks
      ctx.fillStyle = '#065f46';
      ctx.fillRect(margin, margin, innerW, innerH);

      ctx.strokeStyle = '#047857';
      ctx.lineWidth = 2.5;
      for (let x = -w; x < w * 2; x += 28) {
        ctx.beginPath();
        ctx.moveTo(x, 0); ctx.lineTo(x + h, h); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, h); ctx.lineTo(x + h, 0); ctx.stroke();
      }
    } else {
      // Classic Blue Casino Crosshatch (Default)
      ctx.fillStyle = '#1e3a8a';
      ctx.fillRect(margin, margin, innerW, innerH);

      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 2;
      for (let x = -w; x < w * 2; x += 18) {
        ctx.beginPath();
        ctx.moveTo(x, 0); ctx.lineTo(x + h, h); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, h); ctx.lineTo(x + h, 0); ctx.stroke();
      }

      // Center Medallion
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, 60, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#1e3a8a';
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, 48, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('♠ ♥', w / 2, h / 2 - 8);
      ctx.fillText('♦ ♣', w / 2, h / 2 + 14);
    }

    ctx.restore();

    // Inner Framing Line
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.strokeRect(margin + 8, margin + 8, innerW - 16, innerH - 16);
  }

  /**
   * Sets card hover state with smooth vertical lift.
   * @param {boolean} hovered
   */
  setHover(hovered) {
    if (this.isHovered === hovered || this._animating) return;
    this.isHovered = hovered;
    this.updateElevation();
  }

  /**
   * Sets card selection state (elevates further with golden highlight).
   * @param {boolean} selected
   */
  setSelected(selected) {
    this.isSelected = selected;
    if (this.highlightMesh) {
      this.highlightMesh.isVisible = selected;
    }
    this.updateElevation();
  }

  /**
   * Recomputes vertical elevation based on base height, hover, and selection.
   */
  updateElevation() {
    let targetY = this._baseY;
    if (this.isSelected) {
      targetY += this._selectElev;
    } else if (this.isHovered) {
      targetY += (this.isDeck ? 1.4 : this._hoverElev);
    }
    if (this.rootNode && this.rootNode.position) {
      this.rootNode.position.y = targetY;
    }
  }

  /**
   * Animates card translation and rotation towards target coordinates.
   * @param {BABYLON.Vector3} targetPos
   * @param {BABYLON.Vector3} targetRot
   * @param {number} [duration=260]
   * @param {Function} [onComplete]
   */
  animateTo(targetPos, targetRot, duration = 260, onComplete = null) {
    if (!this.rootNode) return;
    if (this._animObserver && this.scene) {
      this.scene.onBeforeRenderObservable.remove(this._animObserver);
      this._animObserver = null;
    }
    this._animating = true;

    const startPos = (this.rootNode.position && typeof this.rootNode.position.clone === 'function')
      ? this.rootNode.position.clone()
      : { x: this.rootNode.position?.x || 0, y: this.rootNode.position?.y || 0, z: this.rootNode.position?.z || 0 };
    const startRot = (this.rootNode.rotation && typeof this.rootNode.rotation.clone === 'function')
      ? this.rootNode.rotation.clone()
      : { x: this.rootNode.rotation?.x || 0, y: this.rootNode.rotation?.y || 0, z: this.rootNode.rotation?.z || 0 };
    const startTime = performance.now();

    const observer = this.scene.onBeforeRenderObservable.add(() => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1.0, elapsed / duration);
      // Smooth cubic ease-out
      const ease = 1 - Math.pow(1 - t, 3);

      if (this.BABYLON.Vector3 && typeof this.BABYLON.Vector3.Lerp === 'function') {
        this.rootNode.position = this.BABYLON.Vector3.Lerp(startPos, targetPos, ease);
        this.rootNode.rotation = this.BABYLON.Vector3.Lerp(startRot, targetRot, ease);
      } else {
        if (this.rootNode.position) {
          this.rootNode.position.x = startPos.x + (targetPos.x - startPos.x) * ease;
          this.rootNode.position.y = startPos.y + (targetPos.y - startPos.y) * ease;
          this.rootNode.position.z = startPos.z + (targetPos.z - startPos.z) * ease;
        }
        if (this.rootNode.rotation) {
          this.rootNode.rotation.x = startRot.x + (targetRot.x - startRot.x) * ease;
          this.rootNode.rotation.y = startRot.y + (targetRot.y - startRot.y) * ease;
          this.rootNode.rotation.z = startRot.z + (targetRot.z - startRot.z) * ease;
        }
      }

      if (t >= 1.0) {
        if (this.scene && this._animObserver === observer) {
          this.scene.onBeforeRenderObservable.remove(observer);
          this._animObserver = null;
        }
        this._animating = false;
        if (this.rootNode.position) {
          this._baseY = this.rootNode.position.y;
        }
        if (typeof onComplete === 'function') onComplete();
      }
    });
    this._animObserver = observer;
  }

  /**
   * Sets card face-up state and updates textures so card face is only visible when revealed.
   * @param {boolean} faceUp
   */
  setFaceUp(faceUp) {
    if (faceUp && (this._wasTurnedDown || this.card?.faceDown)) {
      this.faceUp = false;
      this.options.faceUp = false;
      this.updateCardTextures();
      return;
    }
    this.faceUp = Boolean(faceUp);
    this.options.faceUp = this.faceUp;
    this.updateCardTextures();
  }

  /**
   * Sets card revealed state (e.g. at showdown).
   * @param {boolean} revealed
   */
  setRevealed(revealed) {
    this.options.revealed = Boolean(revealed);
    this.updateCardTextures();
  }

  /**
   * Updates card properties, textures, and metadata in place without mesh recreation.
   * @param {Object} cardData
   * @param {Object} [options={}]
   */
  updateCard(cardData, options = {}) {
    this.card = cardData || this.card;
    if (options.width != null) this.width = options.width;
    if (options.height != null) this.height = options.height;
    if (options.backStyle != null && options.backStyle !== this.backStyle) {
      this.backStyle = options.backStyle;
    }
    if (options.owner != null) this.owner = options.owner;
    if (options.isLocal != null) this.isLocal = Boolean(options.isLocal);
    if (this._wasTurnedDown || this.card?.faceDown) {
      this.faceUp = false;
      this.options.faceUp = false;
    } else if (options.faceUp != null) {
      this.faceUp = Boolean(options.faceUp);
      this.options.faceUp = this.faceUp;
    }
    if (options.revealed != null) this.options.revealed = Boolean(options.revealed);
    if (options.interactive != null) {
      this.isInteractive = Boolean(options.interactive);
      if (this.frontMesh) this.frontMesh.isPickable = this.isInteractive;
      if (this.backMesh) this.backMesh.isPickable = this.isInteractive;
      if (!this.isInteractive && this.isHovered) {
        this.setHover(false);
      }
    }
    if (options.cutIndex != null) this.cutIndex = options.cutIndex;
    if (options.isDeck != null) this.isDeck = Boolean(options.isDeck);
    if (options.isCutFan != null) this.isCutFan = Boolean(options.isCutFan);
    if (options.onClick != null) this.onClick = options.onClick;
    if (options.onHover != null) this.onHover = options.onHover;

    if (this.frontMesh?.metadata) {
      this.frontMesh.metadata.card = this.card;
      this.frontMesh.metadata.cardMesh = this;
      this.frontMesh.metadata.isDeck = this.isDeck;
      this.frontMesh.metadata.isCutFan = this.isCutFan;
      this.frontMesh.metadata.cutIndex = this.cutIndex;
    }
    if (this.backMesh?.metadata) {
      this.backMesh.metadata.card = this.card;
      this.backMesh.metadata.cardMesh = this;
      this.backMesh.metadata.isDeck = this.isDeck;
      this.backMesh.metadata.isCutFan = this.isCutFan;
      this.backMesh.metadata.cutIndex = this.cutIndex;
    }

    this.updateCardTextures();
  }

  /**
   * Sets the 3D position and rotation of the card, with optional smooth animation.
   * @param {number} posX
   * @param {number} posY
   * @param {number} posZ
   * @param {number} rotPitch
   * @param {number} rotYaw
   * @param {number} rotRoll
   * @param {boolean} [smooth=false]
   * @param {number} [duration=200]
   * @param {Function} [onComplete]
   */
  setTransform(posX, posY, posZ, rotPitch, rotYaw, rotRoll, smooth = false, duration = 200, onComplete = null) {
    if (!this.rootNode) return;
    this._baseY = posY;
    const targetY = posY + (this.isSelected ? this._selectElev : (this.isHovered ? (this.isDeck ? 1.4 : this._hoverElev) : 0));

    if (smooth && !this._animating) {
      const curPos = this.rootNode.position;
      const dist = Math.hypot(curPos.x - posX, curPos.y - targetY, curPos.z - posZ);
      if (dist > 0.05) {
        const targetPos = new this.BABYLON.Vector3(posX, targetY, posZ);
        const targetRot = new this.BABYLON.Vector3(rotPitch, rotYaw, rotRoll);
        this.animateTo(targetPos, targetRot, duration, onComplete);
        return;
      }
    }
    if (!this._animating) {
      this.rootNode.position.set(posX, targetY, posZ);
      this.rootNode.rotation.set(rotPitch, rotYaw, rotRoll);
      if (typeof onComplete === 'function') onComplete();
    }
  }

  /**
   * Updates card front texture based on whether the face is legitimately revealed.
   * Unrevealed cards (e.g. opponents' hands or face-down deck/crib) display card back on both sides,
   * completely preventing players from seeing card faces when the 3D room is rotated.
   */
  updateCardTextures() {
    if (!this.frontMat || !this.scene || !this.BABYLON) return;
    if (this.isFaceRevealed) {
      const frontTex = CardMesh3D.getCardFaceTexture(this.scene, this.BABYLON, this.card);
      if (frontTex) {
        frontTex.hasAlpha = false;
        this.frontMat.albedoTexture = frontTex;
        if (this.frontMat.albedoColor) {
          this.frontMat.albedoColor = new this.BABYLON.Color3(1, 1, 1);
        }
      } else {
        this.frontMat.albedoColor = new this.BABYLON.Color3(0.98, 0.98, 0.98);
      }
    } else {
      const backTex = CardMesh3D.getCardBackTexture(this.scene, this.BABYLON, this.backStyle);
      if (backTex) {
        backTex.hasAlpha = false;
        this.frontMat.albedoTexture = backTex;
        if (this.frontMat.albedoColor) {
          this.frontMat.albedoColor = new this.BABYLON.Color3(1, 1, 1);
        }
      } else {
        this.frontMat.albedoColor = new this.BABYLON.Color3(0.12, 0.22, 0.55);
      }
    }
  }

  /**
   * Flips card smoothly face-up or face-down.
   * @param {boolean} faceUp
   * @param {number} [duration=240]
   * @param {Function} [onComplete]
   */
  flip(faceUp, duration = 240, onComplete = null) {
    if (faceUp && (this._wasTurnedDown || this.card?.faceDown)) {
      this.faceUp = false;
      this.options.faceUp = false;
      this.updateCardTextures();
      return;
    }
    this.faceUp = Boolean(faceUp);
    this.options.faceUp = this.faceUp;
    this.updateCardTextures();
    const targetRotY = faceUp ? 0 : Math.PI;
    const curRot = (this.rootNode.rotation && typeof this.rootNode.rotation.clone === 'function')
      ? this.rootNode.rotation.clone()
      : { x: this.rootNode.rotation?.x || 0, y: this.rootNode.rotation?.y || 0, z: this.rootNode.rotation?.z || 0 };
    const targetRot = new this.BABYLON.Vector3(curRot.x, targetRotY, curRot.z);
    this.animateTo(this.rootNode.position.clone(), targetRot, duration, onComplete);
  }

  /**
   * Disposes all meshes, textures, materials, and observables cleanly.
   */
  dispose() {
    try {
      if (this._animObserver && this.scene) {
        this.scene.onBeforeRenderObservable.remove(this._animObserver);
        this._animObserver = null;
      }
      if (this.frontMesh) {
        if (this.frontMesh.metadata) this.frontMesh.metadata = null;
        this.frontMesh.dispose();
      }
      if (this.backMesh) {
        if (this.backMesh.metadata) this.backMesh.metadata = null;
        this.backMesh.dispose();
      }
      if (this.coreMesh) this.coreMesh.dispose();
      if (this.highlightMesh) this.highlightMesh.dispose();
      if (this.frontMat) this.frontMat.dispose();
      if (this.backMat) this.backMat.dispose();
      if (this.coreMat) this.coreMat.dispose();
      if (this.highlightMat) this.highlightMat.dispose();
      if (this.rootNode) this.rootNode.dispose(false, true);
    } catch (_) {}
  }
}

/**
 * CardEngine3D - Manages spatial layouts, hand fans, starter deck, crib, and play piles.
 */
export class CardEngine3D {
  /**
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {Object} options - { tableSurfaceY, tableW, tableD, settings }
   */
  constructor(scene, BABYLON, options = {}) {
    this.scene = scene;
    this.BABYLON = BABYLON;
    this.options = options;

    this.tableSurfaceY = options.tableSurfaceY != null ? options.tableSurfaceY : -0.88;
    this.tableW = options.tableW || 140;
    this.tableD = options.tableD || 100;
    this.boardBounds = options.boardBounds || null;
    this.chairRoots = options.chairRoots || [];
    this.seatManager = options.seatManager || null;
    this.backStyle = options.settings?.cardBack || options.backStyle || 'classic-blue';

    // Card collections
    this.playerHands = new Map(); // seatId ('p1', 'p2'...) -> Array<CardMesh3D>
    this.playPile = [];           // Array<CardMesh3D> (pegging pile)
    this.starterDeck = [];        // Array<CardMesh3D> (draw/cut deck stack)
    this.starterCard = null;      // CardMesh3D (primary or first cut card)
    this.starterCards = [];       // Array<CardMesh3D> (all cut cards for progressive cuts / multi-starters)
    this.cribCards = [];          // Array<CardMesh3D>
    this.cutDeckFan = [];         // Array<CardMesh3D> (initial deal cut fan)
    this.cutPrompt = null;        // 3D floating cut prompt badge
    this.isCuttable = false;
    this.onCutPromptClick = null;

    this.pointerObserver = null;
    this.hoveredCard = null;

    // Interaction callbacks
    this.onCardClick = options.onCardClick || null;
    this.onCardHover = options.onCardHover || null;

    this.setupPointerInteractions();
  }

  /**
   * Returns primary starter card mesh for backward compatibility.
   * @returns {CardMesh3D|null}
   */
  get starterCardMesh() {
    return this.starterCard || (this.starterCards && this.starterCards[0]) || null;
  }

  /**
   * Sets up 3D pointer raycast picking for hover elevation and card selection.
   */
  setupPointerInteractions() {
    if (!this.scene || !this.BABYLON) return;

    let moveRafPending = false;
    let lastPointerX = 0;
    let lastPointerY = 0;

    // Predicate filters out room walls, floor, chairs, and board geometry from raycasting
    const cardRaycastPredicate = (mesh) => {
      return Boolean(mesh && mesh.isPickable && (mesh.metadata?.cardMesh || mesh.metadata?.isCutPrompt));
    };

    const processPointerHover = (px, py) => {
      const pick = this.scene.pick(px, py, cardRaycastPredicate);
      const meta = pick?.pickedMesh?.metadata;
      const cardMesh = meta?.cardMesh;
      const isCutPrompt = Boolean(meta?.isCutPrompt);

      if (cardMesh && cardMesh.isInteractive) {
        if (this.hoveredCard !== cardMesh) {
          if (this.hoveredCard) this.hoveredCard.setHover(false);
          this.hoveredCard = cardMesh;
          this.hoveredCard.setHover(true);
          if (typeof this.onCardHover === 'function') this.onCardHover(cardMesh.card, true);
        }
        if (typeof document !== 'undefined') {
          const canvas = this.scene?.getEngine?.()?.getRenderingCanvas?.();
          if (canvas) canvas.style.cursor = 'pointer';
        }
      } else if (isCutPrompt) {
        if (typeof document !== 'undefined') {
          const canvas = this.scene?.getEngine?.()?.getRenderingCanvas?.();
          if (canvas) canvas.style.cursor = 'pointer';
        }
      } else {
        if (this.hoveredCard) {
          this.hoveredCard.setHover(false);
          if (typeof this.onCardHover === 'function') this.onCardHover(this.hoveredCard.card, false);
          this.hoveredCard = null;
        }
        if (typeof document !== 'undefined') {
          const canvas = this.scene?.getEngine?.()?.getRenderingCanvas?.();
          if (canvas) canvas.style.cursor = 'default';
        }
      }
    };

    this.pointerObserver = this.scene.onPointerObservable.add((pointerInfo) => {
      const type = pointerInfo.type;

      // 1. Throttled Hover Detection via requestAnimationFrame
      if (type === this.BABYLON.PointerEventTypes.POINTERMOVE) {
        lastPointerX = this.scene.pointerX;
        lastPointerY = this.scene.pointerY;
        if (!moveRafPending) {
          moveRafPending = true;
          const scheduleRaf = (typeof requestAnimationFrame === 'function')
            ? requestAnimationFrame
            : (cb) => setTimeout(cb, 16);
          scheduleRaf(() => {
            moveRafPending = false;
            if (this.scene && !this.scene.isDisposed) {
              processPointerHover(lastPointerX, lastPointerY);
            }
          });
        }
      }

      // 2. Click Selection & Play with predicate-based raycasting
      if (type === this.BABYLON.PointerEventTypes.POINTERPICK) {
        const pick = this.scene.pick(this.scene.pointerX, this.scene.pointerY, cardRaycastPredicate);
        const meta = pick?.pickedMesh?.metadata;
        const cardMesh = meta?.cardMesh;

        if (cardMesh && cardMesh.isInteractive) {
          if (typeof cardMesh.onClick === 'function') {
            cardMesh.onClick(cardMesh.card, cardMesh);
          } else {
            cardMesh.setSelected(!cardMesh.isSelected);
            if (typeof this.onCardClick === 'function') {
              this.onCardClick(cardMesh.card, cardMesh);
            }
          }
        } else if (meta?.isCutPrompt) {
          if (typeof meta.onClick === 'function') {
            meta.onClick();
          } else if (typeof this.onCutPromptClick === 'function') {
            this.onCutPromptClick();
          }
        }
      }
    });
  }

  /**
   * Resolves the anchor position and rotation for a player's hand fan in 3D space.
   * Accurately anchors each player's hand on the table surface directly in front of their chair,
   * properly oriented and spaced.
   * @param {string} seat - 'p1', 'p2', etc.
   * @param {boolean} isLocal
   * @returns {{ posX: number, posY: number, posZ: number, angle: number, pitch: number, yaw: number, cardWidth: number, cardHeight: number }}
   */
  getSeatHandAnchor(seat, isLocal) {
    const tableW = this.tableW || 120.0;
    const tableD = this.tableD || 100.0;
    const edgeOffset = 13.5; // Offset inward from table perimeter rim toward center

    let seatIdx = 0;
    if (seat && seat.startsWith('p')) {
      const parsed = parseInt(seat.slice(1), 10);
      if (!isNaN(parsed) && parsed >= 1) seatIdx = parsed - 1;
    }

    let chairRoot = null;
    if (this.chairRoots && this.chairRoots[seatIdx]) {
      chairRoot = this.chairRoots[seatIdx];
    } else if (this.seatManager?.seats?.get(seat)?.chairRoot) {
      chairRoot = this.seatManager.seats.get(seat).chairRoot;
    }

    let angle = 0;
    if (chairRoot && chairRoot.rotation && typeof chairRoot.rotation.y === 'number') {
      angle = chairRoot.rotation.y;
    } else {
      // Standard radial chair angles:
      // p1 (0): South (0)
      // p2 (1): North (PI)
      // p3 (2): West (1.5*PI) or North-West
      // p4 (3): East (0.5*PI)
      if (seatIdx === 0) angle = 0;
      else if (seatIdx === 1) angle = Math.PI;
      else if (seatIdx === 2) angle = 1.5 * Math.PI;
      else if (seatIdx === 3) angle = 0.5 * Math.PI;
    }

    const cardWidth = isLocal ? 14.5 : 13.0;
    const cardHeight = isLocal ? 20.3 : 18.2;
    const pitch = isLocal ? 0.42 : -0.42;

    // Prevent cards from embedding into the table by elevating the center
    // by half the projected card height plus table surface clearance.
    const halfH = cardHeight / 2;
    const bottomOffset = halfH * Math.cos(Math.abs(pitch));
    const tableClearance = isLocal ? 1.4 : 0.8;
    const posY = this.tableSurfaceY + bottomOffset + tableClearance;

    // Position on the table surface directly in front of the player's chair
    const posX = Math.sin(angle) * (tableW / 2 - edgeOffset);
    const posZ = -Math.cos(angle) * (tableD / 2 - edgeOffset);

    return {
      posX,
      posY,
      posZ,
      angle,
      pitch,
      yaw: angle,
      cardWidth,
      cardHeight
    };
  }

  /**
   * Arranges cards into an ergonomic hand fan in front of a player's seat.
   * @param {string} seat - 'p1', 'p2', etc.
   * @param {Array<Object>} cards
   * @param {Object} [options={}]
   * @returns {Array<CardMesh3D>}
   */
  renderHandFan(seat, cards = [], options = {}) {
    if (!cards || !cards.length) {
      this.clearHand(seat);
      return [];
    }

    const isLocal = (options.isLocal != null) ? Boolean(options.isLocal) : (seat === 'p1');
    const count = cards.length;

    const anchor = this.getSeatHandAnchor(seat, isLocal);
    const cardWidth = options.width || anchor.cardWidth;
    const cardHeight = options.height || anchor.cardHeight;

    // Fan Arc Parameters
    const maxFanAngle = Math.min(0.28, count * 0.055); // Radians fan arc
    const cardSpacing = isLocal ? Math.min(8.0, 42.0 / Math.max(1, count)) : Math.min(6.5, 34.0 / Math.max(1, count));

    // Tangent and radial vectors along the table perimeter at the chair angle
    const cosA = Math.cos(anchor.angle);
    const sinA = Math.sin(anchor.angle);

    const isCardFaceUp = isLocal || options.faceUp === true;
    const isCardInteractive = (options.interactive !== undefined) ? Boolean(options.interactive) : isLocal;
    const isOppRevealed = (!isLocal && options.faceUp === true);
    const pitch = isOppRevealed ? (Math.PI / 2) : anchor.pitch;
    const yaw = isOppRevealed ? 0 : anchor.yaw;

    // Collect existing meshes for this seat to diff & reuse
    const existingMeshes = this.playerHands.get(seat) || [];
    const existingById = new Map();
    existingMeshes.forEach(m => {
      if (m && m.rootNode && !m.rootNode.isDisposed) {
        existingById.set(m.cardId, m);
      }
    });

    const newHandMeshes = [];
    const usedMeshSet = new Set();

    cards.forEach((c, idx) => {
      const normIdx = (count > 1) ? (idx / (count - 1) - 0.5) : 0;
      const fanAngle = normIdx * maxFanAngle;

      // Spread cards tangentially along the chair's front edge
      const spread = normIdx * (count * cardSpacing);
      const arcInward = Math.abs(normIdx) * 1.8;

      const posX = anchor.posX + cosA * spread - sinA * arcInward;
      const posZ = anchor.posZ + sinA * spread + cosA * arcInward;
      const posY = anchor.posY + idx * 0.16; // Prevent z-fighting with slight stagger

      const cId = c?.id || `${c?.rankName || c?.r || c?.rank || 'A'}${c?.suit || c?.s || 'S'}`;
      let cardMesh = existingById.get(cId);

      if (cardMesh && !usedMeshSet.has(cardMesh)) {
        usedMeshSet.add(cardMesh);
        // Diffing: reuse existing mesh in-place without rebuilding
        cardMesh.updateCard(c, {
          width: cardWidth,
          height: cardHeight,
          faceUp: isCardFaceUp,
          backStyle: this.backStyle,
          owner: seat,
          isLocal: isLocal,
          interactive: isCardInteractive,
          revealed: isOppRevealed
        });
        cardMesh.setTransform(posX, posY, posZ, pitch, yaw, -fanAngle, false);
      } else {
        cardMesh = new CardMesh3D(this.scene, this.BABYLON, c, {
          width: cardWidth,
          height: cardHeight,
          faceUp: isCardFaceUp,
          backStyle: this.backStyle,
          owner: seat,
          isLocal: isLocal,
          interactive: isCardInteractive,
          revealed: isOppRevealed
        });
        cardMesh.rootNode.position.set(posX, posY, posZ);
        cardMesh.rootNode.rotation.set(pitch, yaw, -fanAngle);
        cardMesh._baseY = posY;
        usedMeshSet.add(cardMesh);
      }

      newHandMeshes.push(cardMesh);
    });

    // Dispose only the removed cards (e.g. card discarded or played to table)
    existingMeshes.forEach(m => {
      if (!usedMeshSet.has(m)) {
        try { m.dispose(); } catch (_) {}
      }
    });

    this.playerHands.set(seat, newHandMeshes);
    return newHandMeshes;
  }

  /**
   * Renders all played pegging cards face up on the table directly in front of the player.
   * Spreads all played cards from left to right in chronological order between the player's
   * hand fan and the central board, completely clear of the board and face-up.
   * Reuses existing played card meshes to prevent allocation stalls during rapid pegging.
   * @param {Array<Object>} playedCards - Array of played cards or { who, card } entries
   * @param {Object} [options={}]
   */
  renderPlayPile(playedCards = [], options = {}) {
    if (!playedCards || !playedCards.length) {
      this.clearPlayPile();
      return;
    }

    const localSeat = options.localSeat || this.localSeat || 'p1';
    const turnSeat = options.turnSeat || options.activeSeat || options.seat || localSeat;
    const isLocal = (turnSeat === localSeat || turnSeat === 'p1');
    const anchor = this.getSeatHandAnchor(turnSeat, isLocal);
    const count = playedCards.length;

    this.playPileTurnSeat = turnSeat;

    const cardW = 13.5;
    const cardH = 18.9;

    // Inward direction from the player's chair toward table center
    const sinA = Math.sin(anchor.angle);
    const cosA = Math.cos(anchor.angle);
    const centerDist = Math.hypot(anchor.posX, anchor.posZ);
    const inwardDist = Math.max(30.0, Math.min(42.0, centerDist * 0.42));
    const inwardX = -sinA * inwardDist;
    const inwardZ = cosA * inwardDist;

    // Tangent unit vector across the player's front
    const tanX = cosA;
    const tanZ = sinA;

    // Horizontal spread in front of player
    const cardSpacing = Math.min(5.2, 45.0 / Math.max(1, count));

    // Map existing play pile cards by ID for diffing
    const existingById = new Map();
    this.playPile.forEach(m => {
      if (m && m.rootNode && !m.rootNode.isDisposed) {
        existingById.set(m.cardId, m);
      }
    });

    const newPlayPile = [];
    const usedMeshSet = new Set();

    playedCards.forEach((item, idx) => {
      const cardData = item?.card || item;
      const who = item?.who || item?.owner || 'p1';

      const spreadOffset = (idx - (count - 1) * 0.5) * cardSpacing;
      const posX = anchor.posX + inwardX + tanX * spreadOffset;
      const posZ = anchor.posZ + inwardZ + tanZ * spreadOffset;
      const posY = this.tableSurfaceY + 0.12 + idx * 0.06;
      const subtleRot = ((idx * 17) % 5 - 2) * (Math.PI / 180);
      const targetRotYaw = anchor.yaw + subtleRot;

      const cId = cardData?.id || `${cardData?.rankName || cardData?.r || cardData?.rank || 'A'}${cardData?.suit || cardData?.s || 'S'}`;
      let cardMesh = existingById.get(cId);
      const wasFlippedDown = Boolean(
        cardData?.faceDown ||
        item?.faceDown ||
        (cardMesh && cardMesh._wasTurnedDown) ||
        (existingById.get(cId) && existingById.get(cId)._wasTurnedDown)
      );
      const isFaceUp = wasFlippedDown
        ? false
        : ((options.faceUp !== undefined) ? Boolean(options.faceUp) : (item?.faceUp !== undefined ? Boolean(item.faceUp) : true));
      const rotY = isFaceUp ? 0 : Math.PI;

      if (cardMesh && !usedMeshSet.has(cardMesh)) {
        usedMeshSet.add(cardMesh);
        if (wasFlippedDown) {
          cardMesh._wasTurnedDown = true;
          if (cardMesh.faceUp && typeof cardMesh.flip === 'function') {
            cardMesh.flip(false, 260);
          } else {
            cardMesh.setFaceUp(false);
          }
        } else if (cardMesh.faceUp !== isFaceUp && typeof cardMesh.flip === 'function') {
          cardMesh.flip(isFaceUp, 260);
        } else {
          cardMesh.updateCard(cardData, {
            width: cardW,
            height: cardH,
            faceUp: isFaceUp,
            backStyle: this.backStyle,
            interactive: false,
            owner: who
          });
        }
        cardMesh.setTransform(posX, posY, posZ, Math.PI / 2, rotY, targetRotYaw, false);
      } else {
        cardMesh = new CardMesh3D(this.scene, this.BABYLON, cardData, {
          width: cardW,
          height: cardH,
          faceUp: isFaceUp,
          backStyle: this.backStyle,
          interactive: false,
          owner: who
        });
        if (wasFlippedDown) cardMesh._wasTurnedDown = true;
        cardMesh.rootNode.position.set(posX, posY, posZ);
        cardMesh.rootNode.rotation.set(Math.PI / 2, rotY, targetRotYaw);
        cardMesh._baseY = posY;
        usedMeshSet.add(cardMesh);
      }

      newPlayPile.push(cardMesh);
    });

    // Dispose only cards no longer in the play pile (e.g. swept on Go/31)
    this.playPile.forEach(m => {
      if (!usedMeshSet.has(m)) {
        try { m.dispose(); } catch (_) {}
      }
    });

    this.playPile = newPlayPile;
  }

  /**
   * Renders the 3D cut starter card and deck stack on the table.
   * Placed to the right of the human player, clear of the central board.
   * Supports interactive deck cutting when isCuttable is true.
   * @param {Object} starterCardData
   * @param {Object} [options={}]
   */
  renderStarterDeck(starterCardData = null, options = {}) {
    const boardLeft = (this.boardBounds?.min?.x != null) ? this.boardBounds.min.x : -14.0;
    const boardBottom = (this.boardBounds?.min?.z != null) ? this.boardBounds.min.z : -8.0;

    const minTableLeft = -this.tableW / 2 + 8.0;
    const playerNearZ = -this.tableD / 2 + 10.0;

    // Placed on the table to the left of the human player, clear of the central board
    const deckX = Math.min(boardLeft - 9.0, Math.max(minTableLeft + 16.0, -32.0));
    const deckZ = Math.min(-24.0, Math.max(playerNearZ, boardBottom - 16.0));
    const deckCardW = 13.5;
    const deckCardH = 18.9;
    const deckStackCount = 8; // Visual cardstock layers
    const isCuttable = Boolean(options.isCuttable);
    this.isCuttable = isCuttable;
    this.onCutStarter = options.onCut || null;

    // 1. Stacked Deck Mesh Layers (reuse existing 8 cards if present and valid)
    const canReuseStack = Array.isArray(this.starterDeck) &&
      this.starterDeck.length === deckStackCount &&
      this.starterDeck.every(c => c && c.rootNode && !c.rootNode.isDisposed);

    if (canReuseStack) {
      for (let i = 0; i < deckStackCount; i++) {
        const posY = this.tableSurfaceY + 0.12 + i * 0.22;
        const stackCard = this.starterDeck[i];
        stackCard.updateCard({ r: 'A', s: 'S' }, {
          width: deckCardW,
          height: deckCardH,
          faceUp: false,
          backStyle: this.backStyle,
          interactive: isCuttable,
          isDeck: true,
          owner: 'deck',
          onClick: () => {
            if (isCuttable && typeof options.onCut === 'function') {
              options.onCut();
            }
          }
        });
        stackCard.setTransform(deckX, posY, deckZ, -Math.PI / 2, 0, 0, false);
      }
    } else {
      if (Array.isArray(this.starterDeck)) {
        this.starterDeck.forEach(c => { try { c.dispose(); } catch (_) {} });
      }
      this.starterDeck = [];
      for (let i = 0; i < deckStackCount; i++) {
        const posY = this.tableSurfaceY + 0.12 + i * 0.22;
        const stackCard = new CardMesh3D(this.scene, this.BABYLON, { r: 'A', s: 'S' }, {
          width: deckCardW,
          height: deckCardH,
          faceUp: false,
          backStyle: this.backStyle,
          interactive: isCuttable,
          isDeck: true,
          owner: 'deck',
          onClick: () => {
            if (isCuttable && typeof options.onCut === 'function') {
              options.onCut();
            }
          }
        });
        stackCard.rootNode.position.set(deckX, posY, deckZ);
        stackCard.rootNode.rotation.set(-Math.PI / 2, 0, 0); // Face down on table
        stackCard._baseY = posY;
        this.starterDeck.push(stackCard);
      }
    }

    const starterList = Array.isArray(starterCardData)
      ? starterCardData.filter(Boolean)
      : (Array.isArray(options.starters) && options.starters.length
        ? options.starters.filter(Boolean)
        : (starterCardData ? [starterCardData] : []));

    // 2. Cut Starter Card(s) (face up, placed directly on top of the deck, fanned if progressive/multiple)
    const existingCutById = new Map();
    (this.starterCards || []).forEach(m => {
      if (m && m.rootNode && !m.rootNode.isDisposed) {
        existingCutById.set(m.cardId, m);
      }
    });

    const newStarterCards = [];
    const usedCutSet = new Set();

    if (starterList.length > 0) {
      const count = starterList.length;
      const isRevealed = (options.starterFace !== false);
      const cutBaseY = this.tableSurfaceY + 0.12 + deckStackCount * 0.22 + 0.08;
      const spacingX = count > 1 ? Math.min(8.5, 26.0 / Math.max(1, count - 1)) : 0;

      starterList.forEach((cardData, idx) => {
        const offsetIdx = count > 1 ? (idx - (count - 1) / 2) : 0;
        const posX = count > 1 ? (deckX + offsetIdx * spacingX) : deckX;
        const posY = cutBaseY + idx * 0.05;
        const posZ = count > 1 ? (deckZ - Math.abs(offsetIdx) * 0.4) : deckZ;
        const rotY = count > 1 ? (offsetIdx * -0.06) : 0;
        const rotX = isRevealed ? Math.PI / 2 : -Math.PI / 2;

        const cId = cardData?.id || `${cardData?.rankName || cardData?.r || cardData?.rank || 'A'}${cardData?.suit || cardData?.s || 'S'}`;
        let cutCard = existingCutById.get(cId);

        if (cutCard && !usedCutSet.has(cutCard)) {
          usedCutSet.add(cutCard);
          cutCard.updateCard({ ...cardData, isCut: true }, {
            width: deckCardW,
            height: deckCardH,
            faceUp: isRevealed,
            backStyle: this.backStyle,
            interactive: false,
            isCutStarter: true,
            cutIndex: idx
          });
          cutCard.setTransform(posX, posY, posZ, rotX, rotY, 0, false);
        } else {
          cutCard = new CardMesh3D(this.scene, this.BABYLON, { ...cardData, isCut: true }, {
            width: deckCardW,
            height: deckCardH,
            faceUp: isRevealed,
            backStyle: this.backStyle,
            interactive: false,
            isCutStarter: true,
            cutIndex: idx
          });
          cutCard.rootNode.position.set(posX, posY, posZ);
          cutCard.rootNode.rotation.set(rotX, rotY, 0);
          cutCard._baseY = posY;
          usedCutSet.add(cutCard);

          // If initially face down during cutting ritual, smoothly flip face up on top of the deck
          if (!isRevealed) {
            setTimeout(() => {
              if (cutCard && cutCard.rootNode && !cutCard.rootNode.isDisposed) {
                cutCard.setFaceUp(true);
                const rotUp = new this.BABYLON.Vector3(Math.PI / 2, rotY, 0);
                cutCard.animateTo(cutCard.rootNode.position.clone(), rotUp, 260 + idx * 90);
              }
            }, 280);
          }
        }

        newStarterCards.push(cutCard);
      });
    }

    // Dispose old starter cards no longer part of the cut
    (this.starterCards || []).forEach(m => {
      if (!usedCutSet.has(m)) {
        try { m.dispose(); } catch (_) {}
      }
    });

    this.starterCards = newStarterCards;
    this.starterCard = this.starterCards[0] || null;

    // 3. Floating 3D Cut Prompt Badge when cutting is active
    if (isCuttable && starterList.length === 0) {
      const promptText = (options.cutCount && options.cutCount > 1)
        ? `✂️ Cut ${options.cutCount} Cards`
        : '✂️ Cut Deck';
      if (!this.cutPrompt || !this.cutPrompt.mesh || this.cutPrompt.mesh.isDisposed) {
        this.createCutPrompt(deckX, this.tableSurfaceY + 7.5, deckZ, options.onCut, promptText);
      } else {
        if (this.cutPrompt.mesh.metadata) {
          this.cutPrompt.mesh.metadata.onClick = options.onCut;
        }
      }
    } else {
      this.clearCutPrompt();
    }
  }

  /**
   * Creates a floating 3D billboard cut prompt badge above the starter deck.
   * @param {number} x
   * @param {number} y
   * @param {number} z
   * @param {Function} [onCut]
   * @param {string} [promptText='✂️ Cut Deck']
   */
  createCutPrompt(x, y, z, onCut = null, promptText = '✂️ Cut Deck') {
    this.clearCutPrompt();
    if (!this.scene || !this.BABYLON || !this.BABYLON.MeshBuilder) return;

    try {
      const promptNode = new this.BABYLON.TransformNode('cutDeckPromptNode', this.scene);
      promptNode.position.set(x, y, z);

      const planeWidth = Math.max(18.0, promptText.length * 1.35 + 2.0);
      const plane = this.BABYLON.MeshBuilder.CreatePlane('cutDeckPromptMesh', {
        width: planeWidth,
        height: 5.5,
        sideOrientation: this.BABYLON.Mesh.DOUBLESIDE || 2
      }, this.scene);
      plane.parent = promptNode;
      plane.billboardMode = this.BABYLON.Mesh.BILLBOARDMODE_ALL || 7;
      plane.isPickable = true;

      let tex = null;
      if (this.BABYLON.DynamicTexture) {
        const texW = 512;
        const texH = 128;
        tex = new this.BABYLON.DynamicTexture('texCutDeckPrompt', { width: texW, height: texH }, this.scene, true);
        tex.hasAlpha = true;
        const ctx = tex.getContext ? tex.getContext() : null;
        if (ctx) {
          ctx.clearRect(0, 0, texW, texH);
          ctx.fillStyle = '#0f172a';
          ctx.beginPath();
          if (typeof ctx.roundRect === 'function') ctx.roundRect(10, 10, texW - 20, texH - 20, 54);
          else ctx.rect(10, 10, texW - 20, texH - 20);
          ctx.fill();

          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 8;
          ctx.stroke();

          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 44px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(promptText, texW / 2, texH / 2);
          if (typeof tex.update === 'function') tex.update();
        }
      }

      let mat = null;
      if (this.BABYLON.PBRMaterial) {
        mat = new this.BABYLON.PBRMaterial('matCutDeckPrompt', this.scene);
        if (tex) {
          mat.albedoTexture = tex;
          mat.useAlphaFromAlbedoTexture = true;
        }
        mat.unlit = true;
        mat.roughness = 0.3;
        mat.backFaceCulling = false;
        plane.material = mat;
      } else if (this.BABYLON.StandardMaterial) {
        mat = new this.BABYLON.StandardMaterial('matCutDeckPrompt', this.scene);
        if (tex) {
          mat.diffuseTexture = tex;
          mat.emissiveTexture = tex;
          mat.opacityTexture = tex;
        }
        mat.disableLighting = true;
        mat.backFaceCulling = false;
        plane.material = mat;
      }

      plane.metadata = {
        isCutPrompt: true,
        onClick: onCut
      };

      const startY = y;
      const startTime = performance.now();
      let bobObserver = null;
      if (this.scene.onBeforeRenderObservable && typeof this.scene.onBeforeRenderObservable.add === 'function') {
        bobObserver = this.scene.onBeforeRenderObservable.add(() => {
          if (!promptNode || !promptNode.position) return;
          const elapsed = (performance.now() - startTime) / 1000;
          promptNode.position.y = startY + Math.sin(elapsed * 4.2) * 0.45;
        });
      }

      this.cutPrompt = {
        node: promptNode,
        mesh: plane,
        mat,
        tex,
        observer: bobObserver
      };
    } catch (_) {}
  }

  /**
   * Cleans up the floating 3D cut prompt badge.
   */
  clearCutPrompt() {
    if (this.cutPrompt) {
      if (this.cutPrompt.observer && this.scene?.onBeforeRenderObservable) {
        this.scene.onBeforeRenderObservable.remove(this.cutPrompt.observer);
      }
      if (this.cutPrompt.mesh && typeof this.cutPrompt.mesh.dispose === 'function') this.cutPrompt.mesh.dispose();
      if (this.cutPrompt.mat && typeof this.cutPrompt.mat.dispose === 'function') this.cutPrompt.mat.dispose();
      if (this.cutPrompt.tex && typeof this.cutPrompt.tex.dispose === 'function') this.cutPrompt.tex.dispose();
      if (this.cutPrompt.node && typeof this.cutPrompt.node.dispose === 'function') this.cutPrompt.node.dispose();
      this.cutPrompt = null;
    }
  }

  /**
   * Animates cutting the 3D deck: separates top cut packet, slides laterally, and reassembles.
   * @param {Function} [onComplete]
   */
  animateDeckCut(onComplete = null) {
    if (!this.starterDeck || this.starterDeck.length < 4) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    const cutPacket = this.starterDeck.slice(4);
    const lateralShift = 7.0;
    const liftY = 3.6;

    cutPacket.forEach((cardMesh, idx) => {
      const curPos = (cardMesh.rootNode.position && typeof cardMesh.rootNode.position.clone === 'function')
        ? cardMesh.rootNode.position.clone()
        : { x: cardMesh.rootNode.position?.x || 0, y: cardMesh.rootNode.position?.y || 0, z: cardMesh.rootNode.position?.z || 0 };
      const curRot = (cardMesh.rootNode.rotation && typeof cardMesh.rootNode.rotation.clone === 'function')
        ? cardMesh.rootNode.rotation.clone()
        : { x: cardMesh.rootNode.rotation?.x || 0, y: cardMesh.rootNode.rotation?.y || 0, z: cardMesh.rootNode.rotation?.z || 0 };

      const targetUp = new this.BABYLON.Vector3(curPos.x + lateralShift, curPos.y + liftY, curPos.z + 1.2);

      cardMesh.animateTo(targetUp, curRot, 220, () => {
        const targetDown = new this.BABYLON.Vector3(curPos.x + lateralShift, this.tableSurfaceY + 0.12 + idx * 0.22, curPos.z + 1.2);
        cardMesh.animateTo(targetDown, curRot, 180, () => {
          setTimeout(() => {
            const targetFinal = new this.BABYLON.Vector3(curPos.x, curPos.y, curPos.z);
            cardMesh.animateTo(targetFinal, curRot, 240, () => {
              if (idx === cutPacket.length - 1 && typeof onComplete === 'function') {
                onComplete();
              }
            });
          }, 350);
        });
      });
    });
  }

  /**
   * Renders the initial deal cut fan across the table for game start cut-for-deal.
   * @param {Array<Object>} cutDeck
   * @param {Object} cutPicks
   * @param {Object} [options={}]
   */
  renderCutDeckFan(cutDeck = [], cutPicks = {}, options = {}) {
    if (!cutDeck || !cutDeck.length) {
      this.clearCutDeckFan();
      return;
    }

    const count = cutDeck.length;
    const isMyCut = Boolean(options.isMyCut);

    const boardRight = (this.boardBounds?.max?.x != null) ? this.boardBounds.max.x : 28.0;
    const boardBottom = (this.boardBounds?.min?.z != null) ? this.boardBounds.min.z : -15.0;

    const maxTableRight = this.tableW / 2 - 8.0;
    const playerNearZ = -this.tableD / 2 + 12.0;

    let startX, endX, spanX, fanZ;

    if (isMyCut) {
      // In front of human player with floating cut prompt badge
      spanX = Math.min(36.0, Math.max(22.0, count * 1.4));
      startX = -spanX / 2;
      endX = spanX / 2;
      fanZ = Math.min(-24.0, Math.max(playerNearZ + 8.0, boardBottom - 18.0));

      const midIdx = Math.floor(count / 2);
      this.createCutPrompt(0, this.tableSurfaceY + 7.5, fanZ - 5.5, () => {
        if (typeof options.onPickCut === 'function') {
          options.onPickCut(midIdx);
        }
      }, '✂️ Cut the Deck');
    } else {
      this.clearCutPrompt();
      // Placed on the table to the right of the human player, clear of the central board
      startX = Math.max(28.0, Math.min(maxTableRight - 28.0, boardRight + 6.0));
      endX = Math.min(maxTableRight, startX + 30.0);
      spanX = endX - startX;
      fanZ = Math.min(-34.0, Math.max(playerNearZ, boardBottom - 16.0));
    }

    const existingById = new Map();
    (this.cutDeckFan || []).forEach(m => {
      if (m && m.rootNode && !m.rootNode.isDisposed) {
        existingById.set(m.cardId, m);
      }
    });

    const newCutDeckFan = [];
    const usedMeshSet = new Set();

    cutDeck.forEach((card, idx) => {
      const pickedEntry = Object.entries(cutPicks).find(([_, p]) => p && p.id === card.id);
      const isPicked = Boolean(pickedEntry);
      const pickedSeat = pickedEntry ? pickedEntry[0] : null;

      const norm = count > 1 ? (idx / (count - 1)) : 0.5;
      const posX = startX + norm * spanX;
      const posY = this.tableSurfaceY + 0.12 + idx * 0.015;
      const posZ = fanZ + Math.sin(norm * Math.PI) * 2.8;

      let targetX, targetY, targetZ, targetRotPitch, targetRotYaw, targetRotRoll;

      if (isPicked && pickedSeat) {
        const isLocal = (pickedSeat === 'p1');
        const anchor = this.getSeatHandAnchor(pickedSeat, isLocal);
        const sinA = Math.sin(anchor.angle);
        const cosA = Math.cos(anchor.angle);
        const inwardDist = 12.0; // Displayed directly in front of the player
        targetX = anchor.posX - sinA * inwardDist;
        targetY = this.tableSurfaceY + 0.35;
        targetZ = anchor.posZ + cosA * inwardDist;
        targetRotPitch = Math.PI / 2;
        targetRotYaw = 0;
        targetRotRoll = anchor.yaw;
      } else {
        targetX = posX;
        targetY = posY;
        targetZ = posZ;
        targetRotPitch = -Math.PI / 2;
        targetRotYaw = 0;
        targetRotRoll = (norm - 0.5) * 0.35;
      }

      const cId = card?.id || `${card?.rankName || card?.r || card?.rank || 'A'}${card?.suit || card?.s || 'S'}`;
      let cardMesh = existingById.get(cId);

      const cardOpts = {
        faceUp: isPicked,
        backStyle: this.backStyle,
        interactive: !isPicked && isMyCut,
        isCutFan: true,
        cutIndex: idx,
        onClick: () => {
          if (!isPicked && isMyCut && typeof options.onPickCut === 'function') {
            options.onPickCut(idx);
          }
        }
      };

      if (cardMesh && !usedMeshSet.has(cardMesh)) {
        usedMeshSet.add(cardMesh);
        cardMesh.updateCard(card, cardOpts);
        cardMesh.setTransform(targetX, targetY, targetZ, targetRotPitch, targetRotYaw, targetRotRoll, true, 380);
      } else {
        cardMesh = new CardMesh3D(this.scene, this.BABYLON, card, cardOpts);
        cardMesh.rootNode.position.set(targetX, targetY, targetZ);
        cardMesh.rootNode.rotation.set(targetRotPitch, targetRotYaw, targetRotRoll);
        cardMesh._baseY = targetY;
        usedMeshSet.add(cardMesh);
      }

      newCutDeckFan.push(cardMesh);
    });

    (this.cutDeckFan || []).forEach(m => {
      if (!usedMeshSet.has(m)) {
        try { m.dispose(); } catch (_) {}
      }
    });

    this.cutDeckFan = newCutDeckFan;
  }

  /**
   * Clears the initial deal cut fan.
   */
  clearCutDeckFan() {
    this.clearCutPrompt();
    if (this.cutDeckFan) {
      this.cutDeckFan.forEach(c => c.dispose());
      this.cutDeckFan = [];
    }
  }

  /**
   * Renders the 4-card crib stack in the crib zone.
   * Placed to the left of the human player, clear of the central board.
   * @param {Array<Object>} cribCards
   * @param {boolean} [isRevealed=false]
   */
  renderCrib(cribCards = [], isRevealed = false) {
    if (!cribCards || !cribCards.length) {
      this.clearCrib();
      return;
    }

    // Symmetrically placed to the left of the human player, clear of the central board
    const boardLeft = (this.boardBounds?.min?.x != null) ? this.boardBounds.min.x : -14.0;
    const boardBottom = (this.boardBounds?.min?.z != null) ? this.boardBounds.min.z : -8.0;

    const minTableLeft = -this.tableW / 2 + 8.0;
    const playerNearZ = -this.tableD / 2 + 10.0;

    const deckX = Math.min(boardLeft - 9.0, Math.max(minTableLeft + 16.0, -32.0));
    const deckZ = Math.min(-24.0, Math.max(playerNearZ, boardBottom - 16.0));
    const cribX = Math.min(deckX - 16.0, Math.max(minTableLeft + 2.0, deckX - 18.0));
    const cribZ = deckZ;
    const cardW = 13.5;
    const cardH = 18.9;

    const existingById = new Map();
    (this.cribCards || []).forEach(m => {
      if (m && m.rootNode && !m.rootNode.isDisposed) {
        existingById.set(m.cardId, m);
      }
    });

    const newCribCards = [];
    const usedMeshSet = new Set();

    cribCards.forEach((c, idx) => {
      const posY = this.tableSurfaceY + 0.12 + idx * 0.22;
      const rotZ = isRevealed ? (idx * 0.12 - 0.18) : (idx * 0.04);
      const posX = cribX + (isRevealed ? (idx - 1.5) * 5.0 : 0);
      const rotPitch = isRevealed ? Math.PI / 2 : -Math.PI / 2;

      const cId = c?.id || `${c?.rankName || c?.r || c?.rank || 'A'}${c?.suit || c?.s || 'S'}`;
      let cardMesh = existingById.get(cId);

      const cardOpts = {
        width: cardW,
        height: cardH,
        faceUp: isRevealed,
        backStyle: this.backStyle,
        interactive: false,
        isLocal: false,
        owner: 'crib'
      };

      if (cardMesh && !usedMeshSet.has(cardMesh)) {
        usedMeshSet.add(cardMesh);
        cardMesh.updateCard(c, cardOpts);
        cardMesh.setTransform(posX, posY, cribZ, rotPitch, 0, rotZ, false);
      } else {
        cardMesh = new CardMesh3D(this.scene, this.BABYLON, c, cardOpts);
        cardMesh.rootNode.position.set(posX, posY, cribZ);
        cardMesh.rootNode.rotation.set(rotPitch, 0, rotZ);
        cardMesh._baseY = posY;
        usedMeshSet.add(cardMesh);
      }

      newCribCards.push(cardMesh);
    });

    (this.cribCards || []).forEach(m => {
      if (!usedMeshSet.has(m)) {
        try { m.dispose(); } catch (_) {}
      }
    });

    this.cribCards = newCribCards;
  }

  /**
   * Clears cards for a specific hand.
   */
  clearHand(seat) {
    const hand = this.playerHands.get(seat);
    if (hand) {
      hand.forEach(c => c.dispose());
      this.playerHands.delete(seat);
    }
  }

  /**
   * Turns all cards currently in the play pile face down out of the way.
   */
  turnPlayPileFaceDown() {
    if (!this.playPile || !this.playPile.length) return;
    this.playPile.forEach((m) => {
      if (m && m.rootNode && !m.rootNode.isDisposed) {
        m._wasTurnedDown = true;
        if (m.card) m.card.faceDown = true;
        if (m.options) m.options.faceUp = false;
        if (typeof m.flip === 'function' && m.faceUp) {
          m.flip(false, 280);
        } else if (typeof m.setFaceUp === 'function') {
          m.setFaceUp(false);
        }
      }
    });
  }

  /**
   * Animates face-down played cards sweeping out of the way before clearing.
   */
  sweepPlayPile() {
    if (!this.playPile || !this.playPile.length) return;
    this.playPile.forEach((m) => {
      if (m && m.rootNode && !m.rootNode.isDisposed && this.BABYLON) {
        const curPos = m.rootNode.position.clone ? m.rootNode.position.clone() : { x: m.rootNode.position.x, y: m.rootNode.position.y, z: m.rootNode.position.z };
        const targetPos = new this.BABYLON.Vector3(curPos.x + 20, (curPos.y || 0) - 0.2, (curPos.z || 0) + (curPos.z >= 0 ? 12 : -12));
        const curRot = m.rootNode.rotation.clone ? m.rootNode.rotation.clone() : { x: m.rootNode.rotation.x, y: m.rootNode.rotation.y, z: m.rootNode.rotation.z };
        const targetRot = new this.BABYLON.Vector3(curRot.x, curRot.y, (curRot.z || 0) + 0.25);
        if (typeof m.animateTo === 'function') {
          m.animateTo(targetPos, targetRot, 480);
        }
      }
    });
  }

  /**
   * Clears the play pile.
   */
  clearPlayPile() {
    this.playPile.forEach(c => c.dispose());
    this.playPile = [];
  }

  /**
   * Clears the starter deck, cut prompt, and cut card(s).
   */
  clearStarterDeck() {
    this.clearCutPrompt();
    this.starterDeck.forEach(c => c.dispose());
    this.starterDeck = [];
    if (this.starterCards && this.starterCards.length) {
      this.starterCards.forEach(c => c && typeof c.dispose === 'function' && c.dispose());
      this.starterCards = [];
    }
    if (this.starterCard) {
      this.starterCard.dispose();
      this.starterCard = null;
    }
  }

  /**
   * Clears the crib stack.
   */
  clearCrib() {
    this.cribCards.forEach(c => c.dispose());
    this.cribCards = [];
  }

  /**
   * Clears all cards in the entire 3D scene.
   */
  clearAll() {
    this.playerHands.forEach(hand => hand.forEach(c => c.dispose()));
    this.playerHands.clear();
    this.clearPlayPile();
    this.clearStarterDeck();
    this.clearCutDeckFan();
    this.clearCrib();
  }

  /**
   * Disposes the card engine and pointer listeners.
   */
  dispose() {
    this.clearAll();
    this.clearCutPrompt();
    if (this.pointerObserver && this.scene) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }
    CardMesh3D.clearTextureCache();
  }
}
